import { FC, useContext, useMemo, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useTable, Row, UseTableOptions, useSortBy } from 'react-table';
import { DraggableTableRow } from './DraggableTableRow';
import { StaticTableRow } from './StaticTableRow';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  Table as MaUTable,
  Skeleton,
  TableBody,
  useMediaQuery,
} from '@mui/material';
import { HeaderContext } from '../../context';
import { IFullTableColumn } from './Table';
import clsx from 'clsx';

interface IDraggableTable<D extends object = {}>
  extends Pick<UseTableOptions<D>, 'data' | 'columns'> {
  columns: any;
  data: any;
  setData: any;
  stickyHeader?: boolean;
  headerClasses?: string;
  tableSize?: 'small' | 'medium';
  ResponsiveComponent?: FC<any>;
  ResponsiveComponentLoader?: FC;
  isLoading?: boolean;
  handleSort?: (...args: any[]) => any;
  disabledDnD?: boolean;
  cellClasses?: string | ((original: D) => string);
  useTableProps?: UseTableOptions<D>;
  mobileProps?: Record<string, unknown>;
}
export const DraggableTable: FC<IDraggableTable> = ({
  columns,
  data,
  setData,
  stickyHeader = false,
  headerClasses,
  tableSize,
  ResponsiveComponent,
  ResponsiveComponentLoader,
  isLoading,
  handleSort,
  disabledDnD = false,
  cellClasses = '',
  useTableProps = {},
  mobileProps,
}) => {
  const { headerHeight } = useContext(HeaderContext);
  const classes = useStyles({ headerHeight });

  const [activeId, setActiveId] = useState<any>();
  const items = useMemo(() => data?.map(({ id }: any) => id), [data]);
  // Use the state and functions returned from useTable to build your UI
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
    },
    useSortBy
  );
  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  function handleDragStart(event: any) {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event: any) {
    const { active, over } = event;
    if (active.id !== over.id) {
      setData((data: any) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(data, oldIndex, newIndex);
      });
    }

    setActiveId(null);
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  const selectedRow = useMemo(() => {
    if (!activeId) {
      return null;
    }
    //@ts-ignore
    const row = rows.find(({ original }) => original?.id === activeId) as Row<any>;
    prepareRow(row);
    return row;
  }, [activeId, rows, prepareRow]);

  const getCellStyle = (column: IFullTableColumn, type: 'header' | 'cell'): React.CSSProperties => {
    let style: React.CSSProperties = { cursor: 'inherit' };

    if (column.isDate) {
      style = {
        ...style,
      };
    } else if (column.isNumber) {
      style = {
        ...style,
        textAlign: 'right',
        paddingRight: '3rem',
      };
    } else if (column.isCurrency) {
      style = {
        ...style,
      };
    } else if (column.isCentered) {
      style = {
        ...style,
        textAlign: 'center',
      };
    }

    if (column.overrideWidth) {
      style = {
        ...style,
        width: column.overrideWidth,
        maxWidth: column.overrideWidth,
        wordWrap: 'break-word', // IE11
        overflowWrap: 'break-word',
      };
    }

    if (type === 'header') {
      style = {
        ...style,
        fontWeight: 'bold',
        cursor: column.handleClickColumn ? 'pointer' : 'inherit',
      };
    }

    return style;
  };
  const isDesktop = useMediaQuery('(min-width: 960px)');
  // Render the UI for your table
  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
    >
      <MaUTable
        stickyHeader={stickyHeader}
        size={tableSize}
        {...getTableProps()}
        data-no-dnd={true}
      >
        {isDesktop || !ResponsiveComponent ? (
          <>
            <TableHead
              className={clsx(stickyHeader ? classes.stickyTableHeader : '')}
              classes={{ root: classes.tableHeadRoot }}
              sx={{
                '& tr': {
                  borderRadius: '2px',
                  '& th': {
                    fontWeight: 'bold',
                    backgroundColor: theme => theme.frankie.searchBlue,
                    // Nearly white, tinted towards the themefied Frankie searchBlue color
                    color: '#eff4f6!important',
                    '&>span': {
                      color: '#eff4f6!important',
                      '& svg': {
                        color: '#eff4f6!important',
                      },
                    },
                  },
                },
              }}
            >
              {headerGroups.map(headerGroup => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {/** TABLE HEADER COLUMNS */}
                  {headerGroup.headers.map((column: any) => (
                    <TableCell
                      align={column?.columnAlignment ?? column?.isCurrency ? 'right' : 'left'}
                      {...column.getHeaderProps()}
                      title={column.title || ''}
                      className={headerClasses}
                      style={getCellStyle(column, 'header')}
                      onClick={e => {
                        const { handleClickColumn } = column as IFullTableColumn;

                        // Also run column click handler if passed
                        handleClickColumn && handleClickColumn(column.id);

                        handleSort?.(e, column);
                      }}
                    >
                      {(column as IFullTableColumn).sort !== false ||
                      (column as IFullTableColumn).isServerSorted ? (
                        <TableSortLabel
                          active={column.isSorted || (column as IFullTableColumn).isServerSorted}
                          direction={
                            column.isSortedDesc || (column as IFullTableColumn).isServerSortedDesc
                              ? 'desc'
                              : 'asc'
                          }
                        >
                          {column.render('Header')}
                        </TableSortLabel>
                      ) : (
                        column.render('Header')
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <tbody {...getTableBodyProps()}>
              {isLoading ? (
                new Array(3).fill('').map((_, j) => (
                  <TableRow key={`table-row-skeleton-${j}`}>
                    {/** LOADING SKELETON */}
                    {columns.map((column: any, i: number) => (
                      <TableCell
                        align={column?.columnAlignment ?? 'left'}
                        key={`skeleton-cell-${i}`}
                        style={getCellStyle(column as IFullTableColumn, 'cell')}
                      >
                        {column.hideLoad ? null : <Skeleton />}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                  {rows.map((row, i) => {
                    prepareRow(row);

                    return (
                      <DraggableTableRow
                        //@ts-ignore
                        key={row.original?.id}
                        row={row}
                        disabledDnD={disabledDnD}
                      />
                    );
                  })}
                </SortableContext>
              )}
            </tbody>
          </>
        ) : (
          <>
            <TableBody>
              {/** TABLE BODY FROM RESPONSIVE COMPONENT */}
              {isLoading ? (
                <TableRow>
                  <TableCell
                    className={clsx(
                      classes.mobileCell,
                      typeof cellClasses === 'function' ? cellClasses({}) : cellClasses
                    )}
                  >
                    {ResponsiveComponentLoader ? <ResponsiveComponentLoader /> : <Skeleton />}
                  </TableCell>
                </TableRow>
              ) : (
                (rows as Row[]).map((row, i) => {
                  prepareRow(row);
                  return (
                    <TableRow
                      {...row.getRowProps()}
                      // {...(rowOnClick ? { onClick: () => rowOnClick(row.original) } : {})}
                      // style={{ display: 'block' }}
                    >
                      <TableCell
                        key={`row-${i}-mobile-cell`}
                        className={clsx(
                          classes.mobileCell,
                          typeof cellClasses === 'function'
                            ? cellClasses(row.original)
                            : cellClasses
                        )}
                        // style={{ display: 'block' }}
                      >
                        <ResponsiveComponent
                          key={`responsive-row-${i}`}
                          {...row}
                          {...useTableProps}
                          {...mobileProps}
                          disabledDnD={disabledDnD}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </>
        )}
      </MaUTable>
      <DragOverlay>
        {activeId && (
          <table style={{ width: '100%' }}>
            <tbody>
              <StaticTableRow row={selectedRow} />
            </tbody>
          </table>
        )}
      </DragOverlay>
    </DndContext>
  );
};

const useStyles = makeStyles<Theme, { headerHeight: number }>(theme => ({
  tableHeadRoot: {
    color: '#eff4f6!important',
  },
  noResults: {
    '& p': {
      marginTop: '1rem',
    },
  },
  stickyHeader: {
    flexGrow: 1,
    maxHeight: '100%',
    overflowX: 'initial',
  },
  stickyTableHeader: {
    position: 'sticky',
    top: ({ headerHeight }) => headerHeight + 'px',
    zIndex: 2,
  },
  borderNone: {
    border: 'none',
  },
  mobileTable: {
    display: 'block',
  },
  mobileCell: {
    padding: 0,
    border: 0,
  },
  centerPagination: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
}));
